import React, { useState } from 'react';

import { cssMap as unboundedCssMap } from '@compiled/react';
import { defineMessages, FormattedMessage } from 'react-intl';

import Button, { LinkButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import {
	ActionName,
	Card,
	ElementName,
	MetadataBlock,
	SmartLinkPosition,
	SmartLinkSize,
	SmartLinkTheme,
	TitleBlock,
} from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';

import { DEFAULT_MAX_SOURCES } from './constants';
import { createLinkClickHandlers } from './create-link-click-handlers';
import { LinkListErroredFallback } from './link-list-errored-fallback';
import type { LinkListComponentProps, LinkListVariantProps } from './types';
import { useLinkListMarkErrored } from './use-link-list-mark-errored';

const styles = cssMap({
	root: {
		borderLeftColor: token('color.border'),
		borderLeftWidth: token('border.width'),
		borderLeftStyle: 'solid',
		borderRightColor: token('color.border'),
		borderRightWidth: token('border.width'),
		borderRightStyle: 'solid',
		position: 'relative',
		paddingTop: token('space.150'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	linkWrapperUnresolved: {
		minHeight: '52px',
	},
	unresolvedSkeletonOverlay: {
		position: 'absolute',
		inset: '0',
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.medium'),
		zIndex: 1,
	},
	borderTop: {
		borderTopColor: token('color.border'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
	},
	borderTopRounded: {
		borderTopLeftRadius: token('radius.medium'),
		borderTopRightRadius: token('radius.medium'),
	},
	borderBottomRounded: {
		borderBottomColor: token('color.border'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomLeftRadius: token('radius.medium'),
		borderBottomRightRadius: token('radius.medium'),
	},
	loadingContainer: {
		paddingTop: token('space.150'),
		paddingLeft: token('space.150'),
	},
	loadingSkeletonRows: {
		flex: '1',
		minWidth: '0px',
	},
	expandWrapper: {
		paddingTop: token('space.100'),
		paddingRight: token('space.150'),
		paddingLeft: token('space.150'),
		paddingBottom: token('space.100'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		height: '40px',
	},
});

const unboundedStyles = unboundedCssMap({
	unboundedTitleWrapper: {
		flex: 1,
		minWidth: '150px',
		overflow: 'hidden',
	},
	unboundedTitleBlock: {
		flex: 1,
		minWidth: '0px',
		overflow: 'hidden',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& div[data-testid="smart-element-group"]': {
			gap: token('space.0'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'span[data-smart-element="CreatedBy"]::before': {
			content: '•',
			marginRight: token('space.100'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& div[data-smart-element="LinkIcon"]': {
			paddingTop: token('space.100'),
			paddingRight: token('space.100'),
			paddingBottom: token('space.100'),
			paddingLeft: token('space.100'),
			borderRadius: token('radius.large'),
			backgroundColor: token('color.background.neutral'),
			marginTop: token('space.negative.050'),
		},
	},
	hideTitleStyles: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'div[data-smart-element="LinkIcon"]': {
			display: 'none',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& div[data-testid="smart-element-group"]': {
			display: 'none',
		},
	},
	unboundedMetadataWrapper: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'span[data-smart-element="Priority"] > span[data-testid="smart-element-badge-label"]': {
			display: 'none',
		},
	},
	metadataModifiedOnSlot: {
		width: '150px',
		flexShrink: 0,
		marginLeft: token('space.150'),
	},
	metadataStateSlot: {
		flexShrink: 0,
		display: 'flex',
		justifyContent: 'flex-end',
		marginLeft: token('space.150'),
	},
	metadataPrioritySlot: {
		width: '32px',
		flexShrink: 0,
		marginLeft: token('space.100'),
	},
	metadataAuthorSlot: {
		width: '60px',
		flexShrink: 0,
		marginLeft: token('space.200'),
	},
	previewSlot: {
		width: '24px',
		flexShrink: 0,
	},
});

const messages = defineMessages({
	link_list_expand: {
		id: 'smart_knowledge_card.link_list_full_expand',
		defaultMessage: 'Show more',
		description:
			'Text shown on a button in the full link list that expands the list to reveal additional hidden items.',
	},
	link_list_collapse: {
		id: 'smart_knowledge_card.link_list_full_collapse',
		defaultMessage: 'Show less',
		description:
			'Text shown on a button in the full link list that collapses the list back to its default shorter view.',
	},
	link_list_view_all: {
		id: 'smart_knowledge_card.link_list_full_view_all',
		defaultMessage: 'View all',
		description:
			'Text shown on a link at the bottom of the full link list that navigates the user to a full list view (used by consumers such as the user profile preview to surface every result on a dedicated page).',
	},
});

const LinkListFullComponent = ({
	url,
	idx,
	borderTopRounded,
	borderBottomRounded,
	onError,
	onLinkClick,
}: LinkListComponentProps) => {
	const [resolved, setResolved] = useState(false);
	const handleResolve = () => setResolved(true);

	const handleError = () => {
		handleResolve();
		onError();
	};

	const { onClick, onAuxClick, onContextMenu } = createLinkClickHandlers(onLinkClick, url, idx);

	return (
		<Box
			xcss={cx(
				styles.root,
				styles.borderTop,
				borderTopRounded && styles.borderTopRounded,
				borderBottomRounded && styles.borderBottomRounded,
				!resolved && styles.linkWrapperUnresolved,
			)}
			testId={`link-list-full-item-${idx}${borderTopRounded ? '-top-rounded' : ''}${
				borderBottomRounded ? '-bottom-rounded' : ''
			}`}
			onAuxClick={onAuxClick}
			onContextMenu={onContextMenu}
		>
			<Card
				appearance="block"
				url={url}
				onClick={onClick}
				ui={{
					theme: SmartLinkTheme.Black,
					clickableContainer: true,
					hideBackground: true,
					hideElevation: true,
					hidePadding: true,
					size: SmartLinkSize.Medium,
					removeBlockRestriction: true,
				}}
				onError={handleError}
				onResolve={handleResolve}
			>
				<Inline spread="space-between" alignBlock="center">
					{/* Left group: title + state, grows to fill available space */}
					<Inline alignBlock="center" xcss={unboundedStyles.unboundedTitleWrapper}>
						<Box xcss={unboundedStyles.unboundedTitleBlock}>
							<TitleBlock
								maxLines={1}
								showActionOnHover
								subtitle={[
									{ name: ElementName.Provider, hideIcon: true },
									{ name: ElementName.CreatedBy },
								]}
								position={SmartLinkPosition.Center}
							/>
						</Box>
						<Box xcss={unboundedStyles.metadataStateSlot}>
							<MetadataBlock maxLines={1} primary={[{ name: ElementName.State }]} />
						</Box>
					</Inline>
					<Inline alignBlock="center" xcss={unboundedStyles.unboundedMetadataWrapper}>
						<Box xcss={unboundedStyles.metadataAuthorSlot}>
							<MetadataBlock maxLines={1} primary={[{ name: ElementName.AuthorGroup }]} />
						</Box>
						<Box xcss={unboundedStyles.metadataModifiedOnSlot}>
							<MetadataBlock maxLines={1} primary={[{ name: ElementName.ModifiedOn }]} />
						</Box>
						<Box xcss={unboundedStyles.metadataPrioritySlot}>
							<MetadataBlock maxLines={1} primary={[{ name: ElementName.Priority }]} />
						</Box>
						<Box xcss={cx(unboundedStyles.hideTitleStyles, unboundedStyles.previewSlot)}>
							<TitleBlock
								maxLines={1}
								actions={[
									{
										name: ActionName.PreviewAction,
										hideContent: true,
									},
								]}
								showActionOnHover
								position={SmartLinkPosition.Center}
							/>
						</Box>
					</Inline>
				</Inline>
			</Card>
			{!resolved && (
				<Box
					xcss={cx(styles.unresolvedSkeletonOverlay, styles.loadingContainer)}
					testId="unresolved-loading-skeleton"
				>
					<Inline space="space.100" alignBlock="center">
						<Skeleton width="16px" height="16px" borderRadius={token('radius.small')} />
						<Stack xcss={styles.loadingSkeletonRows} space="space.100">
							<Skeleton width="40%" height="12px" borderRadius={token('radius.small')} />
							<Skeleton width="70%" height="12px" borderRadius={token('radius.small')} />
						</Stack>
					</Inline>
				</Box>
			)}
		</Box>
	);
};

export const LinkListFull = ({
	urls,
	onLinkClick,
	maxSources = DEFAULT_MAX_SOURCES,
	expandedMaxSources,
	showExpand = true,
	viewAllUrl,
	onViewAllLinkClick,
}: LinkListVariantProps): React.JSX.Element => {
	const [isExpanded, setIsExpanded] = useState(false);
	const { erroredIndices, markErrored } = useLinkListMarkErrored();

	// Clamp `maxSources` to a sensible positive integer — same defensive logic
	// as link-list-compact to keep the API forgiving.
	const safeMaxSources = maxSources >= 1 ? maxSources : DEFAULT_MAX_SOURCES;

	// All non-errored original indices, preserving original positions
	const visibleIndices = urls.map((_, i) => i).filter((i) => !erroredIndices.has(i));

	const expandedCap =
		expandedMaxSources !== undefined && expandedMaxSources > safeMaxSources
			? expandedMaxSources
			: undefined;

	// Only show the expand/collapse button when showExpand is true and there are
	// more items than safeMaxSources. When showExpand is false all items up to
	// safeMaxSources are shown immediately with no toggle.
	const hasMore = showExpand && visibleIndices.length > safeMaxSources;

	const renderedIndices = isExpanded
		? expandedCap !== undefined
			? visibleIndices.slice(0, expandedCap)
			: visibleIndices
		: visibleIndices.slice(0, safeMaxSources);

	const firstVisibleIdx = renderedIndices[0] ?? -1;
	const lastVisibleIdx = renderedIndices[renderedIndices.length - 1] ?? -1;

	// In the edge case that every url has errored, rather than rendering nothing
	// (which looks broken given the surrounding context), fall back to rendering
	// each URL as a plain ADS link so the user still sees the links
	const allUrlsErrored = urls.length > 0 && erroredIndices.size === urls.length;

	if (allUrlsErrored) {
		return (
			<LinkListErroredFallback
				urls={urls}
				onLinkClick={onLinkClick}
				testIdPrefix="link-list-full"
			/>
		);
	}

	const showViewAll = !!viewAllUrl && (!hasMore || isExpanded);
	const showExpandToggle = hasMore && !showViewAll;
	const hasFooter = showExpandToggle || showViewAll;

	return (
		<Stack space="space.0">
			{renderedIndices.map((originalIdx) => (
				<LinkListFullComponent
					key={`${urls[originalIdx]}-${originalIdx}`}
					url={urls[originalIdx]}
					idx={originalIdx}
					borderTopRounded={originalIdx === firstVisibleIdx}
					borderBottomRounded={originalIdx === lastVisibleIdx && !hasFooter}
					onError={() => markErrored(originalIdx)}
					onLinkClick={onLinkClick}
				/>
			))}

			{showExpandToggle && (
				<Inline xcss={cx(styles.expandWrapper, styles.borderBottomRounded)} alignBlock="center">
					<Button appearance="subtle" onClick={() => setIsExpanded(!isExpanded)}>
						<Text color="color.text.subtlest">
							{isExpanded ? (
								<FormattedMessage {...messages.link_list_collapse} />
							) : (
								<FormattedMessage {...messages.link_list_expand} />
							)}
						</Text>
					</Button>
				</Inline>
			)}

			{showViewAll && (
				<Inline
					xcss={cx(styles.expandWrapper, styles.borderBottomRounded)}
					alignBlock="center"
					testId="link-list-full-view-all"
				>
					<LinkButton appearance="subtle" href={viewAllUrl} onClick={onViewAllLinkClick}>
						<Text color="color.text.subtlest">
							<FormattedMessage {...messages.link_list_view_all} />
						</Text>
					</LinkButton>
				</Inline>
			)}
		</Stack>
	);
};
